import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Button, Space, message, Spin, Descriptions, Card, Tag, Input, Popconfirm, Select, Alert,
} from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import {
  fetchPermissionSetById, updatePermissionSet, fetchUserById, fetchPermissionCatalog, deletePermissionSet,
} from '../api';
import PermissionTreeEditor from '../components/PermissionTreeEditor';
import { useDashboardOptions, SCOPE_OPTIONS } from '../../moduleDashboards/constants';

const { Title, Text } = Typography;

const PermissionSetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [permissionSet, setPermissionSet] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(0);
  const [dashboardKey, setDashboardKey] = useState(null);
  const [dashboardMaxScope, setDashboardMaxScope] = useState('BRANCH');
  // Dashboards are per role permission set, so the list comes from the server.
  const {
    options: dashboardOptions, loading: dashboardOptionsLoading, byKey: dashboardByKey,
  } = useDashboardOptions(dashboardKey);

  const [catalog, setCatalog] = useState({});
  const [modules, setModules] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [data, cat] = await Promise.all([
          fetchPermissionSetById(id),
          fetchPermissionCatalog(),
        ]);
        setCatalog(cat.catalog || {});
        setModules(cat.modules || []);

        // Only legacy user-scoped sets have a subject to resolve.
        if (data.subject) {
          try {
            const userData = await fetchUserById(data.subject);
            setSelectedUser(userData?.data || userData);
          } catch { /* subject user may be gone */ }
        }

        setPermissionSet(data);
        setPermissions(data.permissions || []);
        setDescription(data.description || '');
        setPriority(data.priority || 0);
        setDashboardKey(data.dashboardKey || null);
        setDashboardMaxScope(data.dashboardMaxScope || 'BRANCH');
      } catch (err) {
        console.error('Failed to fetch permission set:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const saveChanges = async () => {
    setSaving(true);
    try {
      const cleaned = permissions.map((p) => {
        const deny = p.deny || {};
        const hasDeny = (deny.topLevel || []).length || (deny.viewableFields || []).length
          || (deny.editableFields || []).length;
        const out = {
          module: p.module,
          topLevel: p.topLevel || [],
          viewableFields: p.viewableFields || [],
          editableFields: p.editableFields || [],
        };
        if (hasDeny) out.deny = deny;
        return out;
      });

      const payload = {
        ...permissionSet,
        description,
        priority: Number(priority) || 0,
        dashboardKey: dashboardKey || null,
        dashboardMaxScope: dashboardMaxScope || 'BRANCH',
        permissions: cleaned,
      };
      const res = await updatePermissionSet(id, payload);
      if (res?.failedMessage) {
        message.error(res.failedMessage + (res.errors ? `: ${res.errors.join('; ')}` : ''));
      } else {
        message.success('Permission set updated');
        setPermissionSet(payload);
      }
    } catch (err) {
      message.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deletePermissionSet(id);
      message.success('Permission set deleted successfully');
      navigate('/permission-sets');
    } catch (err) {
      console.error('Failed to delete permission set:', err);
      message.error(err.response?.data?.message || 'Failed to delete permission set');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin size="large" />;
  if (!permissionSet) {
    return <Typography.Text type="danger">Permission Set not found.</Typography.Text>;
  }

  // A role set's own dashboard is keyed by its machine key (falling back to its
  // _id) — the same rule the server uses in constants/dashboards.roleDashboardKey.
  const isRoleSet = permissionSet.scope === 'role';
  const ownDashboardKey = isRoleSet
    ? ((permissionSet.key || '').trim().toUpperCase() || String(permissionSet._id))
    : null;
  const ownDashboard = ownDashboardKey ? dashboardByKey[ownDashboardKey] : null;
  const ownDashboardLabel = ownDashboard
    ? `${ownDashboard.label}${ownDashboard.hasLayout ? '' : ' (not built yet)'}`
    : `${permissionSet.name} (not built yet)`;
  // Bound to some OTHER role's dashboard — overrides its own.
  const pointsElsewhere = isRoleSet && dashboardKey && dashboardKey !== ownDashboardKey;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>
        {permissionSet.name}{' '}
        {permissionSet.scope && <Tag color="geekblue">{permissionSet.scope}</Tag>}
        {permissionSet.isSystem && <Tag color="gold">system</Tag>}
      </Title>

      <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
        {permissionSet.key && <Descriptions.Item label="Key">{permissionSet.key}</Descriptions.Item>}
        {permissionSet.subject && (
          <Descriptions.Item label="Subject (user)">
            {selectedUser ? `${selectedUser.name} (${selectedUser.email || ''}) — ${selectedUser.role || ''}` : permissionSet.subject}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Input
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={permissionSet.isSystem}
        />
        {permissionSet.scope !== 'user' && (
          <Space wrap>
            <Input
              type="number"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              style={{ width: 160 }}
              addonBefore="Priority"
            />
            {/* A ROLE set owns its dashboard — it is built and bound in the
                Dashboard Builder, so there is nothing to pick here. A GROUP set
                has no dashboard of its own, so it may point at a role's. */}
            {isRoleSet ? (
              <Space size={6}>
                <Text type="secondary">Dashboard:</Text>
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>{ownDashboardLabel}</Tag>
                <Button
                  type="link" size="small" icon={<AppstoreOutlined />}
                  onClick={() => navigate('/module-dashboards/builder')}
                >
                  Edit in Dashboard Builder
                </Button>
              </Space>
            ) : (
              <Select
                allowClear
                placeholder="Dashboard (role landing page)"
                value={dashboardKey || undefined}
                onChange={(v) => setDashboardKey(v || null)}
                style={{ width: 300 }}
                options={dashboardOptions}
                loading={dashboardOptionsLoading}
                notFoundContent="No role dashboards yet"
              />
            )}
            <Select
              value={dashboardMaxScope}
              onChange={setDashboardMaxScope}
              style={{ width: 200 }}
              options={SCOPE_OPTIONS.map((s) => ({ value: s.value, label: `Max scope: ${s.label}` }))}
            />
          </Space>
        )}
        {isRoleSet && pointsElsewhere && (
          <Alert
            type="warning" showIcon
            message={`This role is pointed at the “${dashboardByKey[dashboardKey]?.label || dashboardKey}” dashboard instead of its own.`}
            action={(
              <Button size="small" onClick={() => setDashboardKey(ownDashboardKey)}>
                Reset to own dashboard
              </Button>
            )}
          />
        )}
      </Space>

      <Card title="Permissions" size="small" style={{ marginBottom: 16 }}>
        <PermissionTreeEditor
          catalog={catalog}
          modules={modules}
          value={permissions}
          onChange={setPermissions}
        />
      </Card>

      <Space>
        <Button type="primary" onClick={saveChanges} loading={saving}>
          Save Changes
        </Button>
        {!permissionSet.isSystem && (
          <Popconfirm
            title="Delete the permission set"
            description="Are you sure you want to delete this permission set? It will be removed from all assigned users."
            onConfirm={handleDelete}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger loading={saving}>
              Delete Permission Set
            </Button>
          </Popconfirm>
        )}
      </Space>
    </div>
  );
};

export default PermissionSetDetailPage;
